import React, { useCallback, useEffect, useState } from "react";
import HeaderUser from "../../components/HeaderUser";
import { AuthService } from "../../services/AuthService";
import { Campaign, UserService, User } from "../../services/UserService";
import { normalizePhone } from "../../utils/phone";

const getAccountTypeLabel = (role?: string | null) => {
  const normalized = AuthService.normalizeRole(role ?? null);
  switch (normalized) {
    case "ADMIN": return "Administrador";
    case "USER_CLIENT_ADMIN": return "Administrador do cliente";
    case "USER_CLIENT": return "Usuário do cliente";
    case "USER_HUB": return "Usuário Hub";
    default: return normalized ?? "Não informado";
  }
};

const fallbackCampaign: Campaign = { id: 0, name: "Campanha Teste", dashboard_url: "about:blank", platform_id: "TESTE", matomo_id: "TESTE", primary_color: "#0b3d91", secondary_color: "#ffffff", chatbot_enabled: true };

const inputClass = "w-full px-3 py-2.5 rounded-[10px] border border-[#d1d5db] text-sm transition-all duration-200 focus:outline-none focus:border-[#2563eb] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] disabled:bg-[#f3f4f6] disabled:text-[#4b5563] disabled:cursor-not-allowed";
const labelClass = "flex flex-col gap-1.5 font-semibold text-[#111827] text-sm";
const cardClass = "bg-white border border-[#e5e7eb] rounded-xl p-[18px] flex flex-col gap-3 shadow-[0_10px_26px_rgba(15,23,42,0.06)]";
const saveBtnStyle: React.CSSProperties = { background: "linear-gradient(120deg, #0b3d91, #1e68ff)", color: "#fff", border: "none", borderRadius: "12px", padding: "12px 16px", fontWeight: 700, cursor: "pointer", boxShadow: "0 10px 26px rgba(14,52,161,0.35)", transition: "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease" };

const Profile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordToast, setPasswordToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const saveSelectedCampaign = useCallback((campaign: Campaign) => {
    setSelectedCampaign(campaign);
    localStorage.setItem("selectedCampaignId", campaign.id.toString());
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const me = await UserService.getUserWithCamaigns();
        setUser(me);
        const available = me.campaigns && me.campaigns.length > 0 ? me.campaigns : [fallbackCampaign];
        setCampaigns(available);
        if (available.length > 0) {
          const storedId = localStorage.getItem("selectedCampaignId");
          const preferred = storedId ? available.find(c => c.id === Number(storedId)) : null;
          saveSelectedCampaign(preferred || available[0]);
        }
        localStorage.setItem("username", me.username);
        if (me.full_name) localStorage.setItem("fullName", me.full_name);
        if (me.email) localStorage.setItem("userEmail", me.email);
        if (me.phone) localStorage.setItem("userPhone", me.phone);
      } catch (err) { setError("Não foi possível carregar seu perfil."); console.error(err); }
    };
    load();
  }, [saveSelectedCampaign]);

  const handleChange = (field: keyof User, value: string) => {
    if (!user) return;
    if (field === "phone") setPhoneError(null);
    setUser({ ...user, [field]: value });
  };

  const handleSave = async () => {
    if (!user) return;
    setPhoneError(null); setError(null); setMessage(null);
    const fullName = user.full_name?.trim() || "";
    if (!fullName) { setError("Informe seu nome."); return; }
    const { normalized, error: validationError } = normalizePhone(user.phone);
    if (validationError) { setPhoneError(validationError); return; }
    setSaving(true);
    try {
      const updated = await UserService.updateMe({ username: user.username, full_name: fullName, email: user.email, phone: normalized ?? undefined });
      setUser(updated);
      setMessage("Perfil atualizado com sucesso.");
      if (updated.username) localStorage.setItem("username", updated.username);
      if (updated.full_name) localStorage.setItem("fullName", updated.full_name);
      if (updated.email) localStorage.setItem("userEmail", updated.email);
      if (updated.phone) localStorage.setItem("userPhone", updated.phone);
    } catch { setError("Não foi possível salvar. Tente novamente."); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    if (!user) return;
    setPasswordError(null); setPasswordToast(null);
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) return;
    if (passwordForm.new_password !== passwordForm.confirm_password) { setPasswordError("As senhas não conferem."); return; }
    if (passwordForm.new_password.length < 6) { setPasswordError("A nova senha deve ter ao menos 6 caracteres."); return; }
    try {
      await UserService.changeMyPassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
      setPasswordToast({ type: "success", message: "Sua senha foi alterada com sucesso." });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => setPasswordToast(null), 4500);
    } catch {
      setPasswordToast({ type: "error", message: "Não foi possível alterar a senha." });
      setTimeout(() => setPasswordToast(null), 4500);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f7f9fc 0%, #eef2f7 50%, #f9fafc 100%)", color: "#0f172a" }}>
      <HeaderUser campaigns={campaigns} selectedCampaign={selectedCampaign} onSelectCampaign={saveSelectedCampaign} />

      <div className="max-w-[1100px] mx-auto px-[18px] pt-7 pb-14 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="uppercase tracking-[0.08em] font-bold text-[#6b7280] mb-1 text-xs m-0">Conta</p>
            <h2 className="m-0 text-2xl font-bold text-[#0f172a]">Meu perfil</h2>
            <p className="mt-1 text-[#4b5563] m-0">Atualize seus dados e configure seu contato para receber notificações.</p>
          </div>
          <button className="bg-[#eef2f7] text-[#1f2937] border border-[#d1d5db] rounded-[10px] px-3.5 py-2.5 font-bold cursor-pointer transition-colors hover:bg-[#e5e7eb]" onClick={() => AuthService.logout()}>Sair</button>
        </div>

        <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
          {/* Profile card */}
          <div className={cardClass}>
            <h3 className="m-0 font-bold text-[#0f172a]">Informações básicas</h3>
            <div className="flex items-center gap-3 font-semibold text-[#111827]">
              <span className="text-sm">Tipo de conta</span>
              <span className="bg-[#eef2f7] text-[#0b3d91] px-2.5 py-1 rounded-full text-sm font-bold">{getAccountTypeLabel(user?.role ?? AuthService.getUserRole())}</span>
            </div>
            {[
              { label: "Nome de usuário", field: "username" as keyof User, type: "text", disabled: true },
              { label: "Nome completo", field: "full_name" as keyof User, type: "text" },
              { label: "E-mail", field: "email" as keyof User, type: "email" },
              { label: "Telefone (WhatsApp)", field: "phone" as keyof User, type: "tel", placeholder: "+55..." },
            ].map(({ label, field, type, disabled, placeholder }) => (
              <label key={field} className={labelClass}>
                {label}
                <input type={type} value={(user as any)?.[field] || ""} onChange={e => handleChange(field, e.target.value)} disabled={disabled} placeholder={placeholder} className={inputClass} />
                {field === "phone" && phoneError && <span className="text-red-600 text-xs">{phoneError}</span>}
              </label>
            ))}
            <div className="flex items-center flex-wrap gap-3">
              <button style={saveBtnStyle} onClick={handleSave} disabled={saving} className="disabled:opacity-65 disabled:cursor-not-allowed disabled:shadow-none hover:not(:disabled):-translate-y-px">
                {saving ? "Salvando..." : "Salvar alterações"}
              </button>
              {message && <span className="text-[#16a34a] font-semibold text-sm">{message}</span>}
              {error && <span className="text-[#b91c1c] font-semibold text-sm">{error}</span>}
            </div>
          </div>

          {/* Password card */}
          <div className={cardClass}>
            <h3 className="m-0 font-bold text-[#0f172a]">Alterar senha</h3>
            {[
              { label: "Senha atual", key: "current_password" as const },
              { label: "Nova senha", key: "new_password" as const },
              { label: "Confirmar nova senha", key: "confirm_password" as const },
            ].map(({ label, key }) => (
              <label key={key} className={labelClass}>
                {label}
                <input type="password" value={passwordForm[key]} onChange={e => setPasswordForm(prev => ({ ...prev, [key]: e.target.value }))} className={inputClass} />
              </label>
            ))}
            <div className="flex flex-col gap-2">
              <button style={saveBtnStyle} onClick={handlePasswordChange} disabled={saving} className="disabled:opacity-65 disabled:cursor-not-allowed disabled:shadow-none hover:not(:disabled):-translate-y-px">
                Atualizar senha
              </button>
              {passwordError && <span className="text-[#b91c1c] font-semibold text-sm">{passwordError}</span>}
            </div>
          </div>
        </div>

        {/* Toast */}
        {passwordToast && (
          <div
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[260px] max-w-xs min-h-10 px-3.5 py-2 rounded-[10px] flex items-center gap-2 text-center font-bold z-20 ${passwordToast.type === "success" ? "bg-[#ecfdf3] border border-[#bbf7d0] text-[#166534]" : "bg-[#fef2f2] border border-[#fecdd3] text-[#b91c1c]"}`}
            style={{ boxShadow: passwordToast.type === "success" ? "0 10px 22px rgba(34,197,94,0.15)" : "0 10px 22px rgba(248,113,113,0.15)" }}
          >
            <span>{passwordToast.message}</span>
            <button className="bg-transparent border-none cursor-pointer text-base ml-auto px-1.5 py-1 text-inherit" onClick={() => setPasswordToast(null)}>×</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
